import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
    FireIcon, 
    ClockIcon,
    TagIcon,
    HeartIcon,
    ShoppingCartIcon,
    ArrowLeftIcon
} from '@heroicons/react/24/outline';
import { HeartIcon as SolidHeartIcon } from '@heroicons/react/24/solid';
import { useFavorites } from '../context/FavoritesContext';
import { useCart } from '../context/CartContext';
import { toast } from 'react-toastify';
import api from '../utils/api';

function FlashSalePage() {
    const { campaignId } = useParams();
    const { favorites, addToFavorites, removeFromFavorites } = useFavorites();
    const { addToCart } = useCart();
    
    const [flashSale, setFlashSale] = useState(null);
    const [loading, setLoading] = useState(true);
    const [timeRemaining, setTimeRemaining] = useState({});
    const [sortBy, setSortBy] = useState('discount'); // discount, price-low, price-high, name

    useEffect(() => {
        fetchFlashSale();
    }, [campaignId]);

    useEffect(() => {
        if (flashSale) {
            const interval = setInterval(updateCountdown, 1000);
            return () => clearInterval(interval);
        }
    }, [flashSale]);

    const fetchFlashSale = async () => {
        try {
            if (campaignId) {
                // Belirli bir kampanya
                const response = await api(`/api/campaigns/${campaignId}`);
                if (response.data && response.data.campaign_type === 'flash_sale') {
                    setFlashSale(processFlashSaleData(response.data));
                }
            } else {
                // Tüm aktif flash sale'ler
                const response = await api('/api/flash-sale/active');
                if (response.data && response.data.length > 0) {
                    setFlashSale(response.data[0]); // İlk flash sale'i göster
                }
            }
        } catch (error) {
            console.error('Flash sale yüklenirken hata:', error);
            toast.error('Flash sale yüklenemedi');
        } finally {
            setLoading(false);
        }
    };

    const processFlashSaleData = (campaignData) => {
        // Campaign data'yı flash sale formatına çevir
        const products = (campaignData.campaign_products || []).map(cp => {
            const product = cp.products;
            if (!product) return null;

            let discountedPrice = product.price;
            const discountValue = cp.custom_discount_value || campaignData.discount_value;
            
            if (campaignData.discount_type === 'percentage') {
                const discountAmount = (product.price * discountValue) / 100;
                const maxDiscount = campaignData.max_discount_amount;
                const finalDiscount = maxDiscount ? Math.min(discountAmount, maxDiscount) : discountAmount;
                discountedPrice = product.price - finalDiscount;
            } else if (campaignData.discount_type === 'fixed_amount') {
                discountedPrice = Math.max(0, product.price - discountValue);
            }

            return {
                ...product,
                campaign_id: campaignData.id,
                original_price: product.price,
                discounted_price: Math.round(discountedPrice * 100) / 100,
                discount_percentage: Math.round(((product.price - discountedPrice) / product.price) * 100),
                stock_limit: cp.stock_limit,
                sold_count: cp.sold_count,
                remaining_stock: cp.stock_limit ? Math.max(0, cp.stock_limit - cp.sold_count) : null
            };
        }).filter(Boolean);

        return {
            id: campaignData.id,
            name: campaignData.name,
            description: campaignData.description,
            banner_image_url: campaignData.banner_image_url,
            badge_text: campaignData.badge_text || 'FLASH SALE',
            badge_color: campaignData.badge_color,
            show_countdown: campaignData.show_countdown,
            start_date: campaignData.start_date,
            end_date: campaignData.end_date,
            products,
            total_products: products.length
        };
    };

    const updateCountdown = () => {
        if (!flashSale) return;

        const now = new Date().getTime();
        const endTime = new Date(flashSale.end_date).getTime();
        const timeDiff = endTime - now;

        if (timeDiff > 0) {
            const days = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
            const hours = Math.floor((timeDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
            const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));
            const seconds = Math.floor((timeDiff % (1000 * 60)) / 1000);

            setTimeRemaining({
                days: days.toString().padStart(2, '0'),
                hours: hours.toString().padStart(2, '0'),
                minutes: minutes.toString().padStart(2, '0'),
                seconds: seconds.toString().padStart(2, '0'),
                expired: false
            });
        } else {
            setTimeRemaining({
                days: '00',
                hours: '00',
                minutes: '00',
                seconds: '00',
                expired: true
            });
        }
    };

    const handleFavoriteToggle = (productId) => {
        if (favorites.includes(productId)) {
            removeFromFavorites(productId);
            toast.success('Favorilerden kaldırıldı');
        } else {
            addToFavorites(productId);
            toast.success('Favorilere eklendi');
        }
    };

    const handleAddToCart = (product) => {
        // Varyant yoksa, güvenli varsayılan bir varyant ile ekle
        const variant = product.variant || { color: 'Standart', size: 'Std', price_override: null };

        addToCart(
            {
                id: product.id,
                name: product.name,
                price: product.discounted_price, // Flash sale fiyatını kullan
                main_image_url: product.main_image_url,
                campaign_id: product.campaign_id,
                campaign_type: 'flash_sale',
            },
            variant,
            1
        );
        toast.success('Sepete eklendi');
    };

    const getSortedProducts = () => {
        if (!flashSale?.products) return [];

        const products = [...flashSale.products];
        
        switch (sortBy) {
            case 'discount':
                return products.sort((a, b) => b.discount_percentage - a.discount_percentage);
            case 'price-low':
                return products.sort((a, b) => a.discounted_price - b.discounted_price);
            case 'price-high':
                return products.sort((a, b) => b.discounted_price - a.discounted_price);
            case 'name':
                return products.sort((a, b) => a.name.localeCompare(b.name));
            default:
                return products;
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('tr-TR', {
            style: 'currency',
            currency: 'TRY'
        }).format(amount);
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <FireIcon className="w-16 h-16 text-red-500 mx-auto mb-4 animate-bounce" />
                    <div className="text-xl font-semibold">Flash Sale yükleniyor...</div>
                </div>
            </div>
        );
    }

    if (!flashSale) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <FireIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <div className="text-xl font-semibold text-gray-600 mb-4">
                        Aktif Flash Sale bulunamadı
                    </div>
                    <Link
                        to="/"
                        className="inline-flex items-center px-6 py-3 bg-brand-green text-white font-bold rounded-lg hover:bg-brand-green-light transition-colors"
                    >
                        <ArrowLeftIcon className="w-5 h-5 mr-2" />
                        Ana Sayfaya Dön
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Flash Sale Header */}
            <div className="bg-gradient-to-r from-red-500 via-red-600 to-orange-500 text-white">
                <div className="container mx-auto px-4 py-8">
                    <div className="flex flex-col md:flex-row items-center justify-between">
                        <div className="flex items-center mb-4 md:mb-0">
                            <Link
                                to="/"
                                className="mr-4 p-2 bg-white/20 rounded-full hover:bg-white/30 transition-colors"
                            >
                                <ArrowLeftIcon className="w-6 h-6" />
                            </Link>
                            <div className="flex items-center bg-white/20 rounded-full px-4 py-2 mr-4">
                                <FireIcon className="w-8 h-8 mr-2 animate-bounce" />
                                <span className="font-bold text-xl">
                                    {flashSale.badge_text}
                                </span>
                            </div>
                            <div>
                                <h1 className="text-3xl md:text-4xl font-bold">{flashSale.name}</h1>
                                <p className="text-white/90 text-lg">{flashSale.description}</p>
                            </div>
                        </div>

                        {/* Geri Sayım */}
                        {flashSale.show_countdown && timeRemaining && !timeRemaining.expired && (
                            <div className="bg-white/20 rounded-lg p-4">
                                <div className="text-center mb-2">
                                    <ClockIcon className="w-6 h-6 mx-auto mb-1" />
                                    <div className="text-sm">Kampanya Bitiyor</div>
                                </div>
                                <div className="flex items-center space-x-2">
                                    {timeRemaining.days !== '00' && (
                                        <>
                                            <div className="text-center bg-white/20 rounded px-2 py-1">
                                                <div className="text-2xl font-bold">{timeRemaining.days}</div>
                                                <div className="text-xs">Gün</div>
                                            </div>
                                            <div className="text-xl">:</div>
                                        </>
                                    )}
                                    <div className="text-center bg-white/20 rounded px-2 py-1">
                                        <div className="text-2xl font-bold">{timeRemaining.hours}</div>
                                        <div className="text-xs">Saat</div>
                                    </div>
                                    <div className="text-xl">:</div>
                                    <div className="text-center bg-white/20 rounded px-2 py-1">
                                        <div className="text-2xl font-bold">{timeRemaining.minutes}</div>
                                        <div className="text-xs">Dakika</div>
                                    </div>
                                    <div className="text-xl">:</div>
                                    <div className="text-center bg-white/20 rounded px-2 py-1">
                                        <div className="text-2xl font-bold">{timeRemaining.seconds}</div>
                                        <div className="text-xs">Saniye</div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Filtreler */}
            <div className="bg-white shadow-sm border-b">
                <div className="container mx-auto px-4 py-4">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                        <div className="text-lg font-semibold text-gray-800">
                            {flashSale.total_products} Flash Sale Ürünü
                        </div>
                        
                        <div className="flex items-center gap-4">
                            <label className="text-sm text-gray-600">Sırala:</label>
                            <select
                                value={sortBy}
                                onChange={(e) => setSortBy(e.target.value)}
                                className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-red-500 focus:border-transparent"
                            >
                                <option value="discount">En Yüksek İndirim</option>
                                <option value="price-low">Fiyat: Düşükten Yükseğe</option>
                                <option value="price-high">Fiyat: Yüksekten Düşüğe</option>
                                <option value="name">İsim: A-Z</option>
                            </select>
                        </div>
                    </div>
                </div>
            </div>

            {/* Ürün Listesi */}
            <div className="container mx-auto px-4 py-8">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {getSortedProducts().map((product) => (
                        <div key={product.id} className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:scale-105">
                            <div className="relative">
                                <Link to={`/products/${product.id}`}>
                                    <img
                                        src={product.main_image_url || '/placeholder.jpg'}
                                        alt={product.name}
                                        className="w-full h-64 object-cover"
                                    />
                                </Link>
                                
                                {/* İndirim Badge */}
                                <div className="absolute top-3 left-3 bg-red-500 text-white font-bold px-3 py-1 rounded-full text-sm">
                                    %{product.discount_percentage} İNDİRİM
                                </div>
                                
                                {/* Stok Durumu */}
                                {product.stock_limit && (
                                    <div className="absolute top-3 right-3 bg-orange-500 text-white font-bold px-2 py-1 rounded text-xs">
                                        {product.remaining_stock} kaldı!
                                    </div>
                                )}

                                {/* Favori Butonu */}
                                <button
                                    onClick={() => handleFavoriteToggle(product.id)}
                                    className="absolute bottom-3 right-3 p-2 bg-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300"
                                >
                                    {favorites.includes(product.id) ? (
                                        <SolidHeartIcon className="w-5 h-5 text-red-500" />
                                    ) : (
                                        <HeartIcon className="w-5 h-5 text-gray-400 hover:text-red-500" />
                                    )}
                                </button>
                            </div>
                            
                            <div className="p-4">
                                <Link to={`/products/${product.id}`}>
                                    <h3 className="font-semibold text-gray-800 mb-2 line-clamp-2 hover:text-red-600 transition-colors">
                                        {product.name}
                                    </h3>
                                </Link>
                                
                                <div className="flex items-center justify-between mb-3">
                                    <div>
                                        <div className="text-xl font-bold text-red-600">
                                            {formatCurrency(product.discounted_price)}
                                        </div>
                                        <div className="text-sm text-gray-500 line-through">
                                            {formatCurrency(product.original_price)}
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <div className="text-sm text-green-600 font-semibold">
                                            {formatCurrency(product.original_price - product.discounted_price)} tasarruf
                                        </div>
                                    </div>
                                </div>

                                <button
                                    onClick={() => handleAddToCart(product)}
                                    className="w-full bg-red-500 text-white font-bold py-2 px-4 rounded-lg hover:bg-red-600 transition-colors duration-300 flex items-center justify-center"
                                >
                                    <ShoppingCartIcon className="w-5 h-5 mr-2" />
                                    Sepete Ekle
                                </button>
                            </div>
                        </div>
                    ))}
                </div>

                {getSortedProducts().length === 0 && (
                    <div className="text-center py-12">
                        <TagIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                        <h3 className="text-xl font-semibold text-gray-600 mb-2">
                            Flash Sale ürünü bulunamadı
                        </h3>
                        <p className="text-gray-500">
                            Bu kampanyada henüz ürün bulunmuyor.
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
}

export default FlashSalePage;